import {Translator, translate} from 'src/components/TranslationsContext';
import {SWEAnalysisResponse} from 'src/lib/api/analysis';
import {
  EcmwfOutlook,
  GefsOutlook,
  PrecipToDate,
  RegionalReportResponse,
  SiteSynopsis,
  WeeklyOutlookBlock,
} from 'src/lib/api/regionalReport';

// dynamical.org catalog titles.
export const GEFS_FORECAST_NAME = 'NOAA GEFS forecast, 35 day';
export const ECMWF_FORECAST_NAME = 'ECMWF IFS ENS forecast, 46 day, daily, 1.5 degree';
export const GEFS_ANALYSIS_NAME = 'NOAA GEFS analysis';

// The forecast titles contain commas, so prose quotes them to keep sentences parseable.
function quoted(name: string): string {
  return `“${name}”`;
}

const MEDIAN_KEY = 'discharge_q0.5';
const Q90_EXCEEDANCE_KEY = 'discharge_q0.1'; // 90% exceedance (low end)
const Q10_EXCEEDANCE_KEY = 'discharge_q0.9'; // 10% exceedance (high end)

function classifyOutlook(percentOfNormal: number): string {
  if (percentOfNormal < 75) return 'well below normal';
  if (percentOfNormal < 90) return 'below normal';
  if (percentOfNormal <= 110) return 'near normal';
  if (percentOfNormal <= 125) return 'above normal';
  return 'well above normal';
}

function formatPct(value: number | null | undefined): string {
  if (value == null) return 'N/A';
  return `${Math.round(value)}%`;
}

function periodWithYear(label: string, forecastYear: number): string {
  return `${label} ${forecastYear}`;
}

export function primaryTargetPeriod(report: Pick<RegionalReportResponse, 'targetPeriods'>) {
  return (
    report.targetPeriods.find((period) => period.primary) ??
    report.targetPeriods.find((period) => period.label === 'Apr-Sep') ??
    report.targetPeriods[0] ??
    null
  );
}

export type LinkedSentence = {before: string; linkText: string; href: string; after: string};
export type SummarySentence = string | LinkedSentence;

export function generateExecutiveSummary(report: RegionalReportResponse): SummarySentence[] {
  const primary = primaryTargetPeriod(report);
  if (!primary) return [];

  const aggData = report.aggregated[primary.label];
  const medianPct = aggData?.percentOfNormal?.[MEDIAN_KEY];

  if (medianPct == null) {
    const cpcSentence = buildCpcSentence(report);
    return [
      `This report summarizes long-range streamflow forecasts for ${report.sitesIncluded} sites in the region.`,
      ...(cpcSentence ? [cpcSentence] : []),
    ];
  }

  const outlook = classifyOutlook(medianPct);
  const lowPct = aggData?.percentOfNormal?.[Q90_EXCEEDANCE_KEY];
  const highPct = aggData?.percentOfNormal?.[Q10_EXCEEDANCE_KEY];

  const sentences: SummarySentence[] = [];
  const primaryLabelWithYear = periodWithYear(primary.label, report.forecastYear);

  const contributing = aggData?.siteCount ?? report.sitesIncluded;
  const sitesText =
    contributing < report.sitesIncluded
      ? `${contributing} of the ${report.sitesIncluded} sites`
      : `${report.sitesIncluded} sites`;
  sentences.push(
    `Streamflow forecasts across the ${sitesText} included in this report are ${outlook} for the ${primaryLabelWithYear} period, with the median (50% exceedance) forecast at ${formatPct(medianPct)} of the long-term average.`
  );

  if (lowPct != null && highPct != null) {
    const spread = Math.round(highPct - lowPct);
    const uncertainty =
      spread > 50 ? 'substantial' : spread > 25 ? 'moderate' : 'relatively narrow';
    sentences.push(
      `The forecast range spans from ${formatPct(lowPct)} (90% exceedance) to ${formatPct(highPct)} (10% exceedance), reflecting ${uncertainty} uncertainty in the outlook.`
    );
  }

  // Compare across target periods if multiple exist — but skip when the later
  // period's start date is already in the past relative to the report date.
  // Periods overlap (Jan-Jul contains Apr-Jul), so the comparison is not a
  // trend over time.
  if (report.targetPeriods.length > 1) {
    const later = report.targetPeriods[report.targetPeriods.length - 1];
    const reportDate = new Date(report.reportDate);
    const laterStart = new Date(later.startDate);
    const laterIsEntirelyFuture = !isNaN(laterStart.getTime()) && laterStart >= reportDate;
    if (laterIsEntirelyFuture) {
      const laterAgg = report.aggregated[later.label];
      const laterMedian = laterAgg?.percentOfNormal?.[MEDIAN_KEY];
      if (laterMedian != null && medianPct != null) {
        const diff = Math.round(laterMedian - medianPct);
        if (Math.abs(diff) > 5) {
          sentences.push(
            `The ${periodWithYear(later.label, report.forecastYear)} forecast is ${formatPct(laterMedian)} of normal, compared with ${formatPct(medianPct)} for ${primaryLabelWithYear}.`
          );
        }
      }
    }
  }

  // GEFS 35-day anomaly summary (precip + temp averaged over horizon).
  const gefsSentence = buildGefsSentence(report);
  if (gefsSentence) sentences.push(gefsSentence);

  // CPC 8-14 day outlook citation (static link text if available).
  const cpcSentence = buildCpcSentence(report);
  if (cpcSentence) sentences.push(cpcSentence);

  // Closing outlier sentence: name the two sites furthest below normal.
  const outlierSentence = buildOutlierSentence(report, primary.label, primaryLabelWithYear);
  if (outlierSentence) sentences.push(outlierSentence);

  return sentences;
}

function meanOfNumbers(vals: (number | null)[]): number | null {
  const clean = vals.filter((v): v is number => v != null && isFinite(v));
  if (clean.length === 0) return null;
  return clean.reduce((a, b) => a + b, 0) / clean.length;
}

function buildGefsSentence(report: RegionalReportResponse): string | null {
  const g = report.gefsOutlook;
  if (!g) return null;
  const precipAnom = meanOfNumbers(g.precipitation.anomaly);
  const tempAnom = meanOfNumbers(g.temperature.anomaly);
  if (precipAnom == null && tempAnom == null) return null;
  const parts: string[] = [];
  if (precipAnom != null) {
    const dir = precipAnom > 0 ? 'above' : 'below';
    parts.push(
      `precipitation ${dir} climatology by ${Math.abs(precipAnom).toFixed(2)} ${g.precipitation.unit}`
    );
  }
  if (tempAnom != null) {
    const dir = tempAnom > 0 ? 'warmer' : 'cooler';
    parts.push(
      `temperature ${dir} than climatology by ${Math.abs(tempAnom).toFixed(1)} ${g.temperature.unit}`
    );
  }
  const baseline = g.climatologyYears
    ? `the ${g.climatologyYears[0]}–${g.climatologyYears[1]} ${GEFS_ANALYSIS_NAME} average`
    : `a ${GEFS_ANALYSIS_NAME} average`;
  return (
    `The ${quoted(GEFS_FORECAST_NAME)} for the ${g.siteName} shows ${parts.join(' and ')}, on average. ` +
    `These differences use ${baseline}, which combines source periods. ` +
    'Forecast bias is not corrected; they provide approximate weather context, not a seasonal runoff forecast.'
  );
}

function buildCpcSentence(report: RegionalReportResponse): SummarySentence | null {
  const c = report.cpcOutlook;
  if (!c) return null;
  const parts: string[] = [];
  if (c.temperature) {
    const {aboveNormal, belowNormal} = c.temperature;
    const tempDir =
      aboveNormal >= 40
        ? 'above-normal temperature'
        : belowNormal >= 40
          ? 'below-normal temperature'
          : null;
    if (tempDir)
      parts.push(`${Math.round(Math.max(aboveNormal, belowNormal))}% likelihood of ${tempDir}`);
  }
  if (c.precipitation) {
    const {aboveNormal, belowNormal} = c.precipitation;
    const precipDir =
      aboveNormal >= 40
        ? 'above-normal precipitation'
        : belowNormal >= 40
          ? 'below-normal precipitation'
          : null;
    if (precipDir)
      parts.push(`${Math.round(Math.max(aboveNormal, belowNormal))}% likelihood of ${precipDir}`);
  }
  if (parts.length === 0) {
    if (!c.issueDate) return null;
    return {
      before: `The NOAA CPC 8–14 day outlook (issued ${c.issueDate}) is available `,
      linkText: 'here',
      href: c.sourceUrl,
      after: ' for regional context.',
    };
  }
  return `NOAA CPC 8–14 day outlook${c.issueDate ? ` (issued ${c.issueDate})` : ''} indicates ${parts.join(' and ')} over the basin.`;
}

function buildOutlierSentence(
  report: RegionalReportResponse,
  primaryLabel: string,
  primaryLabelWithYear: string
): string | null {
  const sitesWithPct = report.sites
    .map((s) => ({
      name: s.name,
      pct: report.synopsis[s.siteId]?.[primaryLabel]?.percentOfNormal?.[MEDIAN_KEY] ?? null,
    }))
    .filter((s): s is {name: string; pct: number} => s.pct != null);

  const drySites = sitesWithPct.filter((s) => s.pct < 90).sort((a, b) => a.pct - b.pct);
  if (drySites.length < 2) return null;

  const [worst, second] = drySites;
  return `The largest deficits are at ${worst.name} and ${second.name}, with ${worst.name} forecast at ${formatPct(worst.pct)} of normal for the ${primaryLabelWithYear} period.`;
}

/** Returns the aggregated median % of normal for the primary target period, or null. */
export function getAggregatedMedianPct(report: RegionalReportResponse): number | null {
  const primary = primaryTargetPeriod(report);
  if (!primary) return null;
  return report.aggregated[primary.label]?.percentOfNormal?.[MEDIAN_KEY] ?? null;
}

/** Returns a site's median % of normal for the primary target period, or null. */
export function getSiteMedianPct(report: RegionalReportResponse, siteId: string): number | null {
  const primary = primaryTargetPeriod(report);
  if (!primary) return null;
  return report.synopsis[siteId]?.[primary.label]?.percentOfNormal?.[MEDIAN_KEY] ?? null;
}

export function generateSiteNarrative(
  siteName: string,
  siteSynopsis: Record<string, SiteSynopsis>,
  targetPeriods: RegionalReportResponse['targetPeriods'],
  forecastYear: number
): string {
  const primaryPeriod = primaryTargetPeriod({targetPeriods});
  if (!primaryPeriod) return '';

  const primaryLabel = primaryPeriod.label;
  const primary = siteSynopsis[primaryLabel];
  if (!primary) return '';

  const medianPct = primary.percentOfNormal?.[MEDIAN_KEY];
  if (medianPct == null) return `Insufficient data to generate a forecast summary for ${siteName}.`;

  const outlook = classifyOutlook(medianPct);
  const lowPct = primary.percentOfNormal?.[Q90_EXCEEDANCE_KEY];
  const highPct = primary.percentOfNormal?.[Q10_EXCEEDANCE_KEY];

  const sentences: string[] = [];
  const primaryLabelWithYear = periodWithYear(primaryLabel, forecastYear);

  sentences.push(
    `${siteName} is forecast at ${formatPct(medianPct)} of normal for the ${primaryLabelWithYear} period, which is considered ${outlook}.`
  );

  if (lowPct != null && highPct != null) {
    const spread = Math.round(highPct - lowPct);
    if (spread > 40) {
      sentences.push(
        `There is considerable spread in the forecast, ranging from ${formatPct(lowPct)} to ${formatPct(highPct)} of normal across the 90% to 10% exceedance levels.`
      );
    } else if (spread > 20) {
      sentences.push(
        `The forecast ranges from ${formatPct(lowPct)} to ${formatPct(highPct)} of normal, indicating moderate confidence.`
      );
    } else {
      sentences.push(
        `The forecast is fairly tightly constrained, ranging from ${formatPct(lowPct)} to ${formatPct(highPct)} of normal.`
      );
    }
  }

  // Add commentary for additional target periods
  for (const tp of targetPeriods) {
    if (tp === primaryPeriod) continue;
    const tpData = siteSynopsis[tp.label];
    const tpMedian = tpData?.percentOfNormal?.[MEDIAN_KEY];
    if (tpMedian != null && medianPct != null) {
      const diff = Math.round(tpMedian - medianPct);
      if (Math.abs(diff) > 5) {
        const direction = diff > 0 ? 'higher' : 'lower';
        sentences.push(
          `For the ${periodWithYear(tp.label, forecastYear)} period, the forecast is ${direction} at ${formatPct(tpMedian)} of normal.`
        );
      }
    }
  }

  return sentences.join(' ');
}

export function showSWEPercentages(reportDate: string): boolean {
  const month = new Date(reportDate).getUTCMonth() + 1;
  return Number.isFinite(month) && (month < 10 || month >= 12);
}

export function generateSWENarrative(
  siteName: string,
  sweData: SWEAnalysisResponse,
  reportDate = sweData.observedDate ?? '',
  t: Translator = (key, variables) => translate('en', key, variables)
): string {
  const {summary} = sweData.elevationProfile;
  const pctOfAvg = summary.percentOfHistoricalAvg;

  const partial = Math.round(sweData.spatialCoverage) < 100;
  const coverageText = `${Math.round(sweData.spatialCoverage)}% of its area`;

  const asOf = sweData.observedDate ? ` SWANN data as of ${sweData.observedDate}.` : '';

  if (!showSWEPercentages(reportDate)) {
    return (
      t('regionalReportEarlySnow', {
        site: siteName,
        volume: `${Math.round(summary.totalCurrent).toLocaleString()} ${sweData.volumeUnit}`,
        coverage: partial ? t('regionalReportSnowCoverage', {coverage: coverageText}) : '',
      }) + asOf
    );
  }

  if (pctOfAvg == null) {
    return `SWE data is available for the ${siteName} basin${partial ? ` (US part only, ${coverageText})` : ''} but the historical comparison could not be computed.${asOf}`;
  }

  const sentences: string[] = [];

  let condition: string;
  if (pctOfAvg < 50) condition = 'severely below average';
  else if (pctOfAvg < 75) condition = 'well below average';
  else if (pctOfAvg < 90) condition = 'below average';
  else if (pctOfAvg <= 110) condition = 'near average';
  else if (pctOfAvg <= 125) condition = 'above average';
  else condition = 'well above average';

  const subject = partial
    ? `Current snowpack for ${siteName}, measured over the US part of its basin (${coverageText}),`
    : `Current basin snowpack for ${siteName}`;
  sentences.push(
    `${subject} is ${condition} at ${Math.round(pctOfAvg)}% of the historical average (${Math.round(summary.totalCurrent).toLocaleString()} ${sweData.volumeUnit} vs ${Math.round(summary.totalClimatology).toLocaleString()} ${sweData.volumeUnit} climatology).`
  );

  return sentences.join(' ') + asOf;
}

function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${{1: 'st', 2: 'nd', 3: 'rd'}[n % 10] ?? 'th'}`;
}

function formatAmount(value: number, unit: string): string {
  return `${value.toFixed(unit === 'in' ? 2 : 0)} ${unit}`;
}

function formatUtcDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function formatUtcTime(iso: string): string {
  const date = new Date(iso);
  return `${formatUtcDay(iso)} ${String(date.getUTCHours()).padStart(2, '0')}:00 UTC`;
}

export function precipToDateCaption(p: PrecipToDate): string {
  const years = p.historyWaterYears;
  const opening = `Model-estimated basin average for the ${p.siteName} from the ${GEFS_ANALYSIS_NAME}: ${formatAmount(p.currentTotal, p.unit)} through ${formatUtcTime(p.cutoff)}`;
  if (!p.history || p.meanTotal == null || p.rank == null || !years.length) {
    return `${opening}. Too few past years have complete data for a comparison.`;
  }
  return [
    `${opening}, against an average of ${formatAmount(p.meanTotal, p.unit)} over the same elapsed part of water years ${years[0]}–${years[years.length - 1]}.`,
    `${p.rank === 1 ? 'The driest' : `The ${ordinal(p.rank)} driest`} of the ${p.yearsCompared} years compared${p.tiedYears ? ` (tied with ${p.tiedYears})` : ''}.`,
  ].join(' ');
}

/** Blocks still in the future or containing the run date; a stale forecast has none. */
export function upcomingBlocks(blocks: WeeklyOutlookBlock[] | undefined): WeeklyOutlookBlock[] {
  return (blocks ?? []).filter((block) => block.elapsedDays < block.dayCount);
}

export function weeklyOutlookCaption(gefs: GefsOutlook | null, ecmwf: EcmwfOutlook | null): string {
  const sources = [];
  if (gefs && upcomingBlocks(gefs.weekly).length) {
    sources.push(`the ${quoted(GEFS_FORECAST_NAME)} (issued ${formatUtcDay(gefs.issueDate)})`);
  }
  if (ecmwf && upcomingBlocks(ecmwf.weekly).length) {
    sources.push(`the ${quoted(ECMWF_FORECAST_NAME)} (issued ${formatUtcDay(ecmwf.issueDate)})`);
  }
  const years = gefs?.climatologyYears ?? ecmwf?.climatologyYears;
  return [
    `Weekly totals and means for the ${gefs?.siteName ?? ecmwf?.siteName} from ${sources.join(' and ')}.`,
    `Dots show ensemble means, bands the 10th–90th percentile of members, and black ticks the ${years ? `${years[0]}–${years[1]} ` : ''}${GEFS_ANALYSIS_NAME} average.`,
  ].join(' ');
}

export function precipToDateMethodology(p: PrecipToDate | null | undefined): string | null {
  if (!p) return null;
  return `Precipitation since Oct 1: the ${GEFS_ANALYSIS_NAME} is a model-based estimate, not station observations. Early in the water year, totals are small and vary widely from year to year.`;
}

export function weeklyOutlookMethodology(
  gefs: GefsOutlook | null,
  ecmwf: EcmwfOutlook | null
): string | null {
  const hasGefs = upcomingBlocks(gefs?.weekly).length > 0;
  const hasEcmwf = upcomingBlocks(ecmwf?.weekly).length > 0;
  if (!hasGefs && !hasEcmwf) return null;
  const sentences = [];
  const members = [
    ...(hasGefs && gefs?.members ? [`${gefs.members} GEFS`] : []),
    ...(hasEcmwf && ecmwf ? [`${ecmwf.members} ECMWF`] : []),
  ];
  sentences.push(
    `The bands show the spread of the ensemble members${members.length ? ` (${members.join(' and ')})` : ''}, not calibrated probabilities. Days of the first week that have passed still show forecast values.`
  );
  if (hasGefs) {
    sentences.push(
      `Pooled across seasons, the ${quoted(GEFS_FORECAST_NAME)} ran 12–21% wetter than the ${GEFS_ANALYSIS_NAME} one to five weeks ahead over 2020–2026; the difference varies by season and year.`
    );
  }
  if (hasEcmwf) {
    sentences.push(
      `The ${quoted(ECMWF_FORECAST_NAME)} is exploratory: its grid is coarse, and it has only been checked against the ${GEFS_ANALYSIS_NAME} over 2026. In that check, its weekly temperatures beyond about two weeks were no more accurate than the historical average.`
    );
  }
  if (hasGefs) {
    sentences.push(
      hasEcmwf
        ? 'GEFS temperatures have not been checked this way.'
        : 'GEFS weekly temperatures beyond about two weeks have not been checked for skill against the historical average.'
    );
  }
  sentences.push(
    'Model bias is not corrected. These panels give weather context, not seasonal runoff forecasts.'
  );
  return `Weekly outlook: ${sentences.join(' ')}`;
}
